import React from 'react';
import clsx from 'clsx';
import {
  makeStyles, Typography, SvgIcon, Tooltip
} from '@material-ui/core';

import Card from 'src/components/Card';
import {Activity, AlertCircle, AlertTriangle, GitBranch, RefreshCw, Trash2, User} from 'react-feather';
import Node from './Node';
import {IS_COMPLETED, IS_FOCUSED, IS_GREYED} from './constants';
import ConfirmationModal from "../../../components/ConfirmationModal";
import {Box} from "@material-ui/core/index";
import IconRenderer from "../../../components/IconRenderer";
import { Handle, Position } from 'react-flow-renderer';
import UserCell from "../../../components/cells/UserCell";
import {EVENT_ATTRIBUTES_BY_EVENT_TYPE} from "../../../constants/constants";

const useStyles = makeStyles((theme) => ({
  node: {
    width: '100%',
    height: '100%',
    transform: 'scale(1)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
  },
  nodeIsFocused: {
    transform: 'scale(1.2)',
  },
  card: {
    border: `3px solid ${theme.palette.text.secondary}`,
    cursor: 'pointer',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    '&:hover $trash': {
      transform: 'scale(1)'
    },
    '&:hover $fork': {
      transform: 'scale(1)'
    },
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  isFocused: {
    '& $iconBox': {
      borderRight: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
    },
    '& $typographyBox': {
      color: theme.palette.primary.main,
    },
    border: `3px solid ${theme.palette.primary.main}`,
  },
  trash: {
    position: 'absolute',
    border: `2px solid ${theme.palette.text.secondary}`,
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      backgroundColor: (theme.palette.background as any).dark,
    },
    bottom: -7,
    right: -7,
    borderRadius: '100%',
    padding: 4,
    fontSize: 24,
    transform: 'scale(0)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
  },
  triggerIcon: {
    position: 'absolute',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    top: -7,
    left: -7,
    borderRadius: '100%',
    padding: 4,
    fontSize: 24,
  },
  invalidStepIcon: {
    position: 'absolute',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    top: -7,
    right: -7,
    borderRadius: '100%',
    padding: 4,
    fontSize: 24,
  },
  wrapper: {
    display: 'flex',
    flex: '1 1',
  },
  iconBox: {
    display: 'flex',
    borderRight: `2px solid ${theme.palette.text.secondary}`,
    padding: theme.spacing(1),
  },
  typographyBox: {
    display: 'flex',
    flex: '1 1',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(1),
  },
  handle: {
    opacity: 0,
    '&:hover': {
      opacity: 1,
    },
    width: '14px !important',
    height: '14px !important',
  },
  isGreyed: {
    opacity: 0.5,
  },
  isCompleted: {
    border: `3px solid ${theme.palette.primary.main}`,
    opacity: 0.5,
    color: theme.palette.primary.main,
    '& $iconBox': {
      borderRight: `2px solid ${theme.palette.primary.main}`,
    },
  },
  fork: {
    position: 'absolute',
    border: `2px solid ${theme.palette.text.secondary}`,
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      backgroundColor: (theme.palette.background as any).dark,
    },
    bottom: -7,
    borderRadius: '100%',
    padding: 4,
    fontSize: 24,
    transform: 'scale(0)',
    marginLeft: 'auto',
    marginRight: 'auto',
    right: 20,
    textAlign: 'center',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
  },
}));

const StepNode = ({
  data,
  ...rest
}: any) => {
  const classes = useStyles();
  const { step } = data;
  const isFocused = data.nodeStyle === IS_FOCUSED
  const isGreyed = data.nodeStyle === IS_GREYED
  const isCompleted = data.nodeStyle === IS_COMPLETED

  let eventType;
  if (step.action) eventType = step.action.actionType;
  if (step.trigger) eventType = step.trigger.triggerType;

  let iconName = '';
  // @ts-ignore - steps will have either an action OR a trigger
  if (eventType && EVENT_ATTRIBUTES_BY_EVENT_TYPE[eventType]) iconName = EVENT_ATTRIBUTES_BY_EVENT_TYPE[eventType].icon

  return (
    <Node>
      <Box className={clsx(classes.node, {[classes.nodeIsFocused]: isFocused})}>
      <Card
        p={0}
        className={clsx({
          [classes.card]: true,
          [classes.isFocused]: isFocused,
          [classes.isGreyed]: isGreyed,
          [classes.isCompleted]: isCompleted,
        })}
      >
        <Handle className={classes.handle} type="target" position={Position.Top} />
        {
          step.trigger || step.action.requiresApproval ? (
            <Tooltip title="This step requires human input before the workflow can proceed" placement="top">
              <SvgIcon fontSize="small" className={classes.triggerIcon}>
                <User />
              </SvgIcon>
            </Tooltip>
          ) : null
        }
        {
          step.validationErrors.length ? (
            <Tooltip title="This step may fail to execute properly" placement="top">
              <SvgIcon fontSize="small" className={classes.invalidStepIcon}>
                <AlertCircle />
              </SvgIcon>
            </Tooltip>
          ) : null
        }
        <ConfirmationModal
          title="Delete step"
          text="Are you sure you want to proceed? This action cannot be undone."
          callback={() => {
            data.deleteStep(rest.id);
          }}
          render={({ onClick }: any) => (
            <Tooltip title="Delete step" placement="top">
            <SvgIcon fontSize="small" className={classes.trash} onClick={e => {
              e.stopPropagation()
              onClick();
            }}>
              <Trash2 />
            </SvgIcon>
            </Tooltip>
          )}
        />
        <Box className={classes.wrapper}>
          <Box className={classes.iconBox}>
            <IconRenderer iconName={iconName} />
          </Box>
          <Box className={classes.typographyBox}>
            <Typography variant="h6" align="center">{data.label}</Typography>
          </Box>
        </Box>
        <Tooltip title="Create fork" placement="top">
        <SvgIcon fontSize="small" className={classes.fork} onClick={e => {
          e.stopPropagation()
          data.forkStep(rest.id);
        }}>
          <GitBranch />
        </SvgIcon>
        </Tooltip>
        <Handle className={classes.handle} type="source" position={Position.Bottom} />
      </Card>
      </Box>
    </Node>
  );
};

export default StepNode;
